import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { leadService } from '../services/leads'
import type { Lead } from '../../../types'
import { LeadSource } from '../../../types'
import { ArrowLeft, UserPlus, XCircle, ExternalLink, User, RefreshCw, UserCheck } from 'lucide-react'

interface NameConflict {
  customer_id: number
  existing_name: string
  incoming_name: string
  mobile: string
}

function NameConflictButtons({
  nameConflict,
  submitting,
  onResolve,
}: {
  nameConflict: NameConflict
  submitting: boolean
  onResolve: (resolution: string) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        disabled={submitting}
        onClick={() => onResolve('keep_existing')}
        className="btn-secondary flex items-center gap-2 text-sm justify-start"
        style={{ height: 'auto', padding: '8px 12px' }}
      >
        <UserCheck className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
        <span className="flex flex-col items-start">
          <strong>Use existing customer</strong>
          <span className="text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>
            Create lead under <em>{nameConflict.existing_name}</em>
          </span>
        </span>
      </button>
      <button
        type="button"
        disabled={submitting}
        onClick={() => onResolve('use_new')}
        className="btn-secondary flex items-center gap-2 text-sm justify-start"
        style={{ height: 'auto', padding: '8px 12px' }}
      >
        <RefreshCw className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
        <span className="flex flex-col items-start">
          <strong>Update name to "{nameConflict.incoming_name}"</strong>
          <span className="text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>
            Rename <em>{nameConflict.existing_name}</em> and create lead
          </span>
        </span>
      </button>
      <button
        type="button"
        disabled={submitting}
        onClick={() => onResolve('new_customer')}
        className="btn-secondary flex items-center gap-2 text-sm justify-start"
        style={{ height: 'auto', padding: '8px 12px' }}
      >
        <User className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
        <span className="flex flex-col items-start">
          <strong>New customer, same number</strong>
          <span className="text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>
            Mark <em>{nameConflict.existing_name}</em> inactive, create <em>{nameConflict.incoming_name}</em>
          </span>
        </span>
      </button>
    </div>
  )
}

export default function NewLeadPage() {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [existingLead, setExistingLead] = useState<Lead | null>(null)
  const [nameConflict, setNameConflict] = useState<NameConflict | null>(null)

  const [form, setForm] = useState({
    customer_name: '',
    mobile: '',
    vehicle_reg_no: '',
    vehicle_chassis_no: '',
    vehicle_model: '',
    insurance_due_date: '',
    lead_source: LeadSource.RENEWAL,
    language_preference: 'en',
    customer_notes: '',
    whatsapp_opted_in: true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const setErrorField = (field: string, msg: string | null) =>
    setErrors(prev => {
      if (msg === null) {
        const { [field]: _, ...rest } = prev; return rest
      }
      return { ...prev, [field]: msg }
    })

  const set = (field: string, value: unknown) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const handleChange = (field: string, raw: string) => {
    if (field === 'mobile') {
      const cleaned = raw.replace(/[^+\d]/g, '')
      const plusCount = (cleaned.match(/\+/g) || []).length
      const val = plusCount > 1 ? cleaned.replace(/\+/g, '') : cleaned
      set(field, val)
      const digits = val.replace(/\D/g, '')
      if (digits && (digits.length < 10 || digits.length > 15)) {
        setErrorField('mobile', 'Mobile must be 10-15 digits')
      } else {
        setErrorField('mobile', null)
      }
      return
    }
    set(field, raw)
  }

  const handleBlur = (field: string) => {
    if (field === 'mobile') {
      const digits = form.mobile.replace(/\D/g, '')
      if (form.mobile && (digits.length < 10 || digits.length > 15)) {
        setErrorField('mobile', 'Mobile must be 10-15 digits')
      } else {
        setErrorField('mobile', null)
      }
    }
  }

  const buildPayload = (name_resolution?: string) => ({
    customer_name: form.customer_name.trim(),
    mobile: form.mobile.trim(),
    vehicle_reg_no: form.vehicle_reg_no.trim() || undefined,
    vehicle_chassis_no: form.vehicle_chassis_no.trim() || undefined,
    vehicle_model: form.vehicle_model.trim() || undefined,
    insurance_due_date: form.insurance_due_date || undefined,
    lead_source: form.lead_source,
    language_preference: form.language_preference,
    customer_notes: form.customer_notes.trim() || undefined,
    whatsapp_opted_in: form.whatsapp_opted_in,
    dedup_mode: 'skip' as const,
    ...(name_resolution ? { name_resolution } : {}),
  })

  const submitLead = async (name_resolution?: string) => {
    setSubmitting(true)
    setError('')
    setExistingLead(null)
    setNameConflict(null)
    try {
      const lead = await leadService.create(buildPayload(name_resolution))
      navigate(`/leads/${lead.id}`, { replace: true })
    } catch (err: unknown) {
      const resp    = (err as { response?: { status?: number; data?: unknown } })?.response
      const status  = resp?.status
      const data    = resp?.data as Record<string, unknown> | undefined
      const detail  = data?.detail

      if (status === 409 && detail && typeof detail === 'object' && 'conflict' in detail) {
        // Name conflict — existing customer has a different name for this mobile
        setNameConflict((detail as { conflict: NameConflict }).conflict)
      } else if (status === 409) {
        setError('A lead with this mobile and vehicle already exists.')
        const existing = data?.existing_lead as Lead | undefined
        if (existing) setExistingLead(existing)
      } else {
        setError((detail as string | undefined) || 'Failed to create lead')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!form.customer_name.trim()) newErrors.customer_name = 'Customer name is required'
    const digits = form.mobile.replace(/\D/g, '')
    if (!form.mobile.trim()) newErrors.mobile = 'Mobile is required'
    else if (digits.length < 10 || digits.length > 15) newErrors.mobile = 'Mobile must be 10-15 digits'
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return
    await submitLead()
  }

  const legendStyle = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--text-tertiary)',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--border-default)',
    width: '100%',
    display: 'block',
  }

  return (
    <div style={{ maxWidth: '640px' }} className="space-y-6">
      <button
        onClick={() => navigate('/leads')}
        className="flex items-center gap-1 text-sm transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <ArrowLeft className="h-4 w-4" /> Back to Leads
      </button>

      <div>
        <h1 className="page-title flex items-center gap-2">
          <UserPlus className="h-6 w-6" style={{ color: 'var(--brand-600)' }} /> Add Lead
        </h1>
        <p className="page-subtitle mt-1">
          Create a single lead. For bulk import, use CSV upload on the Leads page.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        {/* Generic error */}
        {error && (
          <div className="alert-danger">
            <XCircle className="h-4 w-4 shrink-0" />
            <div className="flex-1">
              <span>{error}</span>
              {existingLead && (
                <button
                  type="button"
                  onClick={() => navigate(`/leads/${existingLead.id}`)}
                  className="ml-2 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2"
                  style={{ color: 'var(--danger-text)' }}
                >
                  View existing lead <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Name conflict — inline panel on desktop only */}
        {nameConflict && (
          <div
            className="hidden md:block space-y-3 rounded-lg p-4"
            style={{ background: 'var(--warning-bg, rgba(234,179,8,0.08))', border: '1px solid var(--warning-border, rgba(234,179,8,0.3))' }}
          >
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              This mobile is already linked to an existing customer
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Mobile <strong>{nameConflict.mobile}</strong> belongs to{' '}
              <strong>{nameConflict.existing_name}</strong> in your system,
              but you entered <strong>{nameConflict.incoming_name}</strong>.
              How would you like to proceed?
            </p>
            <NameConflictButtons nameConflict={nameConflict} submitting={submitting} onResolve={submitLead} />
          </div>
        )}

        {/* Customer details */}
        <fieldset className="space-y-4">
          <legend style={legendStyle}>Customer Details</legend>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Customer Name <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input
                type="text"
                className="input"
                value={form.customer_name}
                onChange={e => set('customer_name', e.target.value)}
                placeholder="Ramesh Kumar"
                required
              />
              {errors.customer_name && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.customer_name}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Mobile <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input
                type="tel"
                className="input"
                value={form.mobile}
                onChange={e => handleChange('mobile', e.target.value)}
                onBlur={() => handleBlur('mobile')}
                placeholder="9876543210"
                required
              />
              {errors.mobile && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.mobile}</p>}
              {!errors.mobile && <p className="form-hint">10-digit mobile or +91XXXXXXXXXX</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Language</label>
              <select
                className="input"
                value={form.language_preference}
                onChange={e => set('language_preference', e.target.value)}
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="ta">Tamil</option>
                <option value="te">Telugu</option>
                <option value="kn">Kannada</option>
                <option value="mr">Marathi</option>
                <option value="gu">Gujarati</option>
                <option value="bn">Bengali</option>
              </select>
            </div>
            <div className="flex items-center gap-3 md:pt-5">
              <input
                id="wa_optin"
                type="checkbox"
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--brand-primary)' }}
                checked={form.whatsapp_opted_in}
                onChange={e => set('whatsapp_opted_in', e.target.checked)}
              />
              <label htmlFor="wa_optin" className="cursor-pointer">
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>WhatsApp opted-in</span>
                <span className="block text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  Sends qualification message on create
                </span>
              </label>
            </div>
          </div>
        </fieldset>

        {/* Vehicle details */}
        <fieldset className="space-y-4">
          <legend style={legendStyle}>Vehicle Details</legend>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Registration No.</label>
              <input
                type="text"
                className="input"
                value={form.vehicle_reg_no}
                onChange={e => set('vehicle_reg_no', e.target.value.toUpperCase())}
                placeholder="MH02AB1234"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Chassis No.</label>
              <input
                type="text"
                className="input"
                value={form.vehicle_chassis_no}
                onChange={e => set('vehicle_chassis_no', e.target.value.toUpperCase())}
                placeholder="MA3ERLF1S00123456"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Vehicle Model</label>
              <input
                type="text"
                className="input"
                value={form.vehicle_model}
                onChange={e => set('vehicle_model', e.target.value)}
                placeholder="Hyundai i20"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Insurance Due Date</label>
              <input
                type="date"
                className="input"
                value={form.insurance_due_date}
                onChange={e => set('insurance_due_date', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        {/* Lead metadata */}
        <fieldset className="space-y-4">
          <legend style={legendStyle}>Lead Metadata</legend>

          <div className="form-group w-full md:w-1/2">
            <label className="form-label">Lead Source</label>
            <select
              className="input"
              value={form.lead_source}
              onChange={e => set('lead_source', e.target.value)}
            >
              {Object.values(LeadSource).map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="input"
              rows={3}
              maxLength={1000}
              value={form.customer_notes}
              onChange={e => set('customer_notes', e.target.value)}
              placeholder="Any special notes about this customer or vehicle…"
            />
            <p className="form-hint">{form.customer_notes.length}/1000</p>
          </div>
        </fieldset>

        <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3 pt-2">
          <button type="button" className="btn-secondary w-full md:w-auto" onClick={() => navigate('/leads')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary w-full md:w-auto" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Lead'}
          </button>
        </div>
      </form>

      {/* Name conflict — bottom sheet on mobile */}
      {nameConflict && (
        <div className="md:hidden">
          {/* Backdrop — reuse design-system overlay */}
          <div className="modal-overlay" onClick={() => setNameConflict(null)} />
          {/* Sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl p-5 space-y-4"
            style={{
              background: 'var(--surface-card)',
              borderTop: '1px solid var(--border-default)',
              boxShadow: 'var(--shadow-modal)',
            }}
          >
            {/* Drag handle */}
            <div className="mx-auto w-10 h-1 rounded-full" style={{ background: 'var(--border-default)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              This mobile is already linked to an existing customer
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <strong>{nameConflict.mobile}</strong> belongs to{' '}
              <strong>{nameConflict.existing_name}</strong>, but you entered{' '}
              <strong>{nameConflict.incoming_name}</strong>. How would you like to proceed?
            </p>
            <NameConflictButtons nameConflict={nameConflict} submitting={submitting} onResolve={submitLead} />
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => setNameConflict(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
